import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { type FlowEdge, type FlowNode, type FundFlow, type GroupAnalysis, groupTint, type WalletGroup } from '@pleco/shared';
import { fmtAge, fmtNum, fmtPct, fmtSol, shortAddr } from '../../lib/fmt.js';

/**
 * Fund-flow diagram: where a wallet's money came from (left: funder of
 * funder → first funder / other senders) and where it went (right:
 * recipients, DEX trading). Every node is clickable and re-centres the
 * diagram on that wallet, so a whole chain can be walked hop by hop.
 *
 * Rendering rules that keep it stable (no hover flicker):
 *  - hover never mounts/unmounts SVG content or changes layout; it only
 *    toggles classes (opacity/colour transitions);
 *  - the tooltip is an always-mounted, pointer-events:none overlay moved by
 *    a ref on pointermove (no React re-render per mouse move);
 *  - leaving an element clears hover after a short grace period, so moving
 *    edge → node doesn't flash the "nothing hovered" state in between;
 *  - layout depends only on the data and the container width.
 */

const SWAP = '__dex_swaps__';

type EdgeKey = 'funding' | 'in' | 'out' | 'before_trading' | 'while_trading' | 'after_exit';
const EDGE_STYLE: Record<EdgeKey, { color: string; label: string }> = {
  funding: { color: '#6d8bff', label: 'First funding' },
  in: { color: '#1fc58e', label: 'Inflow' },
  out: { color: '#f5a35b', label: 'Outflow' },
  before_trading: { color: '#38bdf8', label: 'Before trading' },
  while_trading: { color: '#a78bfa', label: 'While trading' },
  after_exit: { color: '#f6465d', label: 'After exiting' },
};

const KIND: Record<FlowNode['kind'], { accent: string; label: string }> = {
  focus: { accent: '#3d8bff', label: 'selected wallet' },
  wallet: { accent: '#8b9cff', label: 'wallet' },
  cex: { accent: '#f5b13d', label: 'exchange' },
  service: { accent: '#9aa4bd', label: 'service / bot' },
  pool: { accent: '#22d3ee', label: 'DEX pool' },
  program: { accent: '#64748b', label: 'program' },
  unknown: { accent: '#5f6984', label: 'unknown' },
};

interface Dims {
  nodeW: number;
  focusW: number;
  nodeH: number;
  rowGap: number;
  pad: number;
  gapMin: number;
  gapMax: number;
  /** approx glyph widths used for truncation, per font */
  sans: number;
  mono: number;
}
const PANEL: Dims = { nodeW: 172, focusW: 184, nodeH: 54, rowGap: 12, pad: 14, gapMin: 54, gapMax: 150, sans: 6.6, mono: 7.0 };
const FULL: Dims = { nodeW: 214, focusW: 230, nodeH: 62, rowGap: 16, pad: 28, gapMin: 80, gapMax: 170, sans: 7.4, mono: 7.9 };

interface Port {
  y: number;
  w: number;
}
interface LaidEdge {
  i: number;
  e: FlowEdge;
  key: EdgeKey;
  color: string;
  w: number;
  path: string;
  arrow: string;
  hitPath: string;
}
interface LaidNode {
  n: FlowNode;
  x: number;
  y: number;
  w: number;
  h: number;
  col: number;
}

type Hover = { t: 'node'; id: string } | { t: 'edge'; i: number } | undefined;

const colOf = (n: FlowNode) => (n.kind === 'focus' ? 0 : n.address === SWAP ? 1 : Math.max(-2, Math.min(2, n.hop ?? 1)));

function edgeKey(e: FlowEdge, colByAddr: Map<string, number>): EdgeKey {
  if (e.kind === 'funding') return 'funding';
  if (e.phase) return e.phase;
  const df = Math.abs(colByAddr.get(e.from) ?? 1);
  const dt = Math.abs(colByAddr.get(e.to) ?? 1);
  return dt < df ? 'in' : 'out';
}

function edgeAmount(e: FlowEdge): string {
  const v = e.sol > 0 ? `${fmtSol(e.sol)} SOL` : e.tokens ? `${fmtNum(e.tokens)} tok` : e.kind === 'funding' ? 'first funds' : '—';
  return e.txCount > 1 ? `${v} ×${e.txCount}` : v;
}

function clip(s: string, maxChars: number) {
  return s.length > maxChars ? `${s.slice(0, Math.max(1, maxChars - 1))}…` : s;
}

function layoutFlow(flow: FundFlow, containerW: number, d: Dims) {
  const colByAddr = new Map(flow.nodes.map((n) => [n.address, colOf(n)]));
  const vol = new Map<string, number>();
  for (const e of flow.edges) {
    vol.set(e.from, (vol.get(e.from) ?? 0) + e.sol);
    vol.set(e.to, (vol.get(e.to) ?? 0) + e.sol);
  }
  const maxSol = Math.max(0.001, ...flow.edges.map((e) => e.sol));
  const strokeW = (e: FlowEdge) => (e.kind === 'token' && !e.sol ? 1.6 : 1.6 + Math.sqrt(e.sol / maxSol) * 6.4);

  // which side of each node every edge attaches to
  const sides = new Map<string, number[]>(); // `${addr}|L|R` -> edge indices
  const sideOf: { from: 'L' | 'R'; to: 'L' | 'R' }[] = [];
  flow.edges.forEach((e, i) => {
    const ca = colByAddr.get(e.from);
    const cb = colByAddr.get(e.to);
    if (ca === undefined || cb === undefined) return void sideOf.push({ from: 'R', to: 'R' });
    const s = ca < cb ? { from: 'R' as const, to: 'L' as const } : ca > cb ? { from: 'L' as const, to: 'R' as const } : { from: 'R' as const, to: 'R' as const };
    sideOf.push(s);
    for (const [addr, side] of [
      [e.from, s.from],
      [e.to, s.to],
    ] as const) {
      const k = `${addr}|${side}`;
      sides.set(k, [...(sides.get(k) ?? []), i]);
    }
  });

  // node heights grow so stacked edge ports never overlap (sankey style)
  const PORT_GAP = 4;
  const stackH = (addr: string, side: 'L' | 'R') => {
    const list = sides.get(`${addr}|${side}`) ?? [];
    return list.reduce((s, i) => s + strokeW(flow.edges[i]!), 0) + PORT_GAP * Math.max(0, list.length - 1);
  };
  const heightOf = (n: FlowNode) => {
    const base = n.kind === 'focus' ? d.nodeH + 16 : d.nodeH;
    return Math.max(base, Math.ceil(Math.max(stackH(n.address, 'L'), stackH(n.address, 'R')) + 22));
  };

  // columns
  const cols = new Map<number, FlowNode[]>();
  for (const n of flow.nodes) cols.set(colOf(n), [...(cols.get(colOf(n)) ?? []), n]);
  const rank = (n: FlowNode) => (n.address === flow.summary.firstFunder ? 1 : 0);
  for (const list of cols.values())
    list.sort((a, b) => (a.address === SWAP ? 1 : 0) - (b.address === SWAP ? 1 : 0) || rank(b) - rank(a) || (vol.get(b.address) ?? 0) - (vol.get(a.address) ?? 0));
  const used = [...cols.keys()].sort((a, b) => a - b);
  const minCol = used[0] ?? 0;
  const maxCol = used.at(-1) ?? 0;
  const nCols = maxCol - minCol + 1;

  const colW = (c: number) => (c === 0 ? d.focusW : d.nodeW);
  let fixedW = 0;
  for (let c = minCol; c <= maxCol; c++) fixedW += colW(c);
  const gap = nCols > 1 ? Math.max(d.gapMin, Math.min(d.gapMax, (containerW - 2 * d.pad - fixedW) / (nCols - 1))) : 0;
  const colX = new Map<number, number>();
  let cx = d.pad;
  for (let c = minCol; c <= maxCol; c++) {
    colX.set(c, cx);
    cx += colW(c) + gap;
  }
  const width = cx - gap + d.pad;

  const colHeight = (list: FlowNode[]) => list.reduce((s, n) => s + heightOf(n), 0) + d.rowGap * Math.max(0, list.length - 1);
  const height = Math.max(160, ...[...cols.values()].map(colHeight)) + 2 * d.pad;

  const nodes = new Map<string, LaidNode>();
  const placeStack = (c: number, list: FlowNode[]) => {
    let y = (height - colHeight(list)) / 2;
    for (const n of list) {
      const h = heightOf(n);
      nodes.set(n.address, { n, x: colX.get(c)!, y, w: colW(c), h, col: c });
      y += h + d.rowGap;
    }
  };
  // inner columns first (stacked + centred), outer columns aligned to their neighbours
  for (const c of [0, -1, 1]) if (cols.has(c)) placeStack(c, cols.get(c)!);
  for (const c of [-2, 2]) {
    const list = cols.get(c);
    if (!list) continue;
    const inner = c < 0 ? c + 1 : c - 1;
    const want = list.map((n) => {
      const ys = flow.edges
        .filter((e) => e.from === n.address || e.to === n.address)
        .map((e) => nodes.get(e.from === n.address ? e.to : e.from))
        .filter((x): x is LaidNode => !!x && x.col === inner)
        .map((x) => x.y + x.h / 2);
      return { n, h: heightOf(n), cy: ys.length ? ys.reduce((a, b) => a + b, 0) / ys.length : height / 2 };
    });
    want.sort((a, b) => a.cy - b.cy);
    let floor = d.pad;
    const placed = want.map((w) => {
      const y = Math.max(floor, w.cy - w.h / 2);
      floor = y + w.h + d.rowGap;
      return { ...w, y };
    });
    const overflow = floor - d.rowGap - (height - d.pad);
    for (const p of placed) nodes.set(p.n.address, { n: p.n, x: colX.get(c)!, y: p.y - Math.max(0, overflow), w: d.nodeW, h: p.h, col: c });
  }

  // port positions: edges on one side stacked in order of the far end's y
  const ports = new Map<string, Port>(); // `${edgeIndex}|from|to`
  for (const [k, list] of sides) {
    const [addr] = k.split('|') as [string, string];
    const node = nodes.get(addr);
    if (!node) continue;
    const far = (i: number) => {
      const e = flow.edges[i]!;
      const o = nodes.get(e.from === addr ? e.to : e.from);
      return o ? o.y + o.h / 2 : 0;
    };
    // two-way traffic with the same counterparty: rightward lane on top at
    // BOTH ends, so the pair runs in parallel instead of crossing
    const rightward = (i: number) => {
      const e = flow.edges[i]!;
      return (nodes.get(e.from)?.x ?? 0) < (nodes.get(e.to)?.x ?? 0) ? 0 : 1;
    };
    const sorted = [...list].sort((a, b) => far(a) - far(b) || rightward(a) - rightward(b));
    const total = sorted.reduce((s, i) => s + strokeW(flow.edges[i]!), 0) + PORT_GAP * Math.max(0, sorted.length - 1);
    let y = node.y + node.h / 2 - total / 2;
    for (const i of sorted) {
      const w = strokeW(flow.edges[i]!);
      const end = flow.edges[i]!.from === addr ? 'from' : 'to';
      ports.set(`${i}|${end}`, { y: y + w / 2, w });
      y += w + PORT_GAP;
    }
  }

  const colNow = new Map([...nodes].map(([k2, v]) => [k2, v.col]));
  const edges: LaidEdge[] = [];
  flow.edges.forEach((e, i) => {
    const a = nodes.get(e.from);
    const b = nodes.get(e.to);
    const s = sideOf[i]!;
    const pa = ports.get(`${i}|from`);
    const pb = ports.get(`${i}|to`);
    if (!a || !b || !pa || !pb) return;
    const w = strokeW(e);
    const x1 = s.from === 'R' ? a.x + a.w : a.x;
    const tipX = s.to === 'L' ? b.x - 1 : b.x + b.w + 1;
    const dir = s.to === 'L' ? 1 : -1; // direction of travel when entering the target
    const al = 6 + w * 0.7;
    const aw = 3.2 + w * 0.6;
    const x2 = tipX - dir * al;
    const out = s.from === 'R' ? 1 : -1;
    const span = Math.abs(x2 - x1);
    const dx = s.from === s.to ? 70 : Math.max(26, span * 0.5);
    const path = `M${x1},${pa.y} C${x1 + out * dx},${pa.y} ${x2 - dir * dx},${pb.y} ${x2},${pb.y}`;
    const arrow = `M${tipX},${pb.y} L${x2},${pb.y - aw} L${x2},${pb.y + aw} Z`;
    const key = edgeKey(e, colNow);
    edges.push({ i, e, key, color: EDGE_STYLE[key].color, w, path, arrow, hitPath: path });
  });

  return { nodes, edges, width, height };
}

/* ------------------------------------------------------------------ component */

export function FundFlowDiagram({
  flow,
  loading,
  focus,
  analysis,
  onOpen,
  full,
  onToggleFull,
}: {
  /** Last loaded flow; may belong to the previous wallet while `loading`. */
  flow?: FundFlow;
  loading: boolean;
  /** Wallet currently being shown / loaded. */
  focus: string;
  analysis?: GroupAnalysis;
  onOpen: (address: string) => void;
  /** Full-screen is controlled by the parent so it survives hop navigation. */
  full: boolean;
  onToggleFull: () => void;
}) {
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation(); // don't also close the wallet drawer
      onToggleFull();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [full, onToggleFull]);

  const body = (
    <FlowBody flow={flow} loading={loading} focus={focus} analysis={analysis} onOpen={onOpen} full={full} onToggleFull={onToggleFull} />
  );
  if (!full) return body;
  return (
    <>
      {/* keeps the drawer's space while the diagram is shown full screen */}
      <div className="flow-placeholder">
        <span>Fund flow is open in full screen.</span>
        <button type="button" className="flow-btn" onClick={onToggleFull}>
          Restore
        </button>
      </div>
      {createPortal(
        <div className="flow-overlay" role="dialog" aria-modal="true" aria-label="Fund flow">
          <div className="flow-backdrop" onClick={onToggleFull} />
          <div className="flow-full">{body}</div>
        </div>,
        document.body,
      )}
    </>
  );
}

function FlowBody({
  flow,
  loading,
  focus,
  analysis,
  onOpen,
  full,
  onToggleFull,
}: {
  flow?: FundFlow;
  loading: boolean;
  focus: string;
  analysis?: GroupAnalysis;
  onOpen: (address: string) => void;
  full: boolean;
  onToggleFull: () => void;
}) {
  const dims = full ? FULL : PANEL;
  const canvasRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const cw = size.w;
  const [hover, setHoverState] = useState<Hover>();
  const clearTimer = useRef<number | undefined>(undefined);

  useLayoutEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const measure = () => setSize((o) => (o.w === Math.round(el.clientWidth) && o.h === Math.round(el.clientHeight) ? o : { w: Math.round(el.clientWidth), h: Math.round(el.clientHeight) }));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [full]);

  // new data or navigating away -> drop stale hover
  useEffect(() => setHoverState(undefined), [flow, focus]);
  useEffect(() => () => window.clearTimeout(clearTimer.current), []);

  const enter = useCallback((h: Hover) => {
    window.clearTimeout(clearTimer.current);
    setHoverState(h);
  }, []);
  const leave = useCallback(() => {
    window.clearTimeout(clearTimer.current);
    clearTimer.current = window.setTimeout(() => {
      setHoverState(undefined);
      delete tipRef.current?.dataset.placed;
    }, 70);
  }, []);
  const moveTip = useCallback((ev: RPointerEvent) => {
    const tip = tipRef.current;
    const box = canvasRef.current?.getBoundingClientRect();
    if (!tip || !box) return;
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    let x = ev.clientX - box.left + 14;
    let y = ev.clientY - box.top + 16;
    if (x + tw > box.width - 6) x = ev.clientX - box.left - tw - 14;
    if (y + th > box.height - 6) y = ev.clientY - box.top - th - 12;
    tip.style.transform = `translate(${Math.max(6, x)}px, ${Math.max(6, y)}px)`;
    tip.dataset.placed = '1';
  }, []);

  const layout = useMemo(() => (flow && cw ? layoutFlow(flow, cw, dims) : undefined), [flow, cw, dims]);
  // full screen: scale the (vector) diagram up to use the viewport, crisp at any size
  const scale = layout && full && size.h ? Math.max(0.5, Math.min(1.5, (size.w - 16) / layout.width, (size.h - 16) / layout.height)) : 1;

  const groupOf = useCallback(
    (addr: string): WalletGroup | undefined => {
      const id = analysis?.wallets[addr]?.groupId;
      return id ? analysis?.groups.find((g) => g.id === id) : undefined;
    },
    [analysis],
  );

  // what is highlighted for the current hover
  const hot = useMemo(() => {
    if (!hover || !layout) return undefined;
    const nodes = new Set<string>();
    const edges = new Set<number>();
    if (hover.t === 'node') {
      nodes.add(hover.id);
      for (const le of layout.edges)
        if (le.e.from === hover.id || le.e.to === hover.id) {
          edges.add(le.i);
          nodes.add(le.e.from);
          nodes.add(le.e.to);
        }
    } else {
      const le = layout.edges.find((x) => x.i === hover.i);
      if (le) {
        edges.add(le.i);
        nodes.add(le.e.from);
        nodes.add(le.e.to);
      }
    }
    return { nodes, edges };
  }, [hover, layout]);

  const legend = useMemo(() => {
    if (!layout) return [];
    const keys = new Set(layout.edges.map((e) => e.key));
    return (Object.keys(EDGE_STYLE) as EdgeKey[]).filter((k) => keys.has(k));
  }, [layout]);
  const hasToken = !!flow?.edges.some((e) => e.kind === 'token');
  const stale = loading && flow?.focus !== focus;

  return (
    <div className={`flow ${full ? 'is-full' : ''}`}>
      <div className="flow-head">
        {full && (
          <div className="flow-title-row">
            <b>Fund flow</b>
            <span className="mono">{shortAddr(focus, 6, 6)}</span>
            {flow && !stale && (
              <span className="faint">
                received <b className="buy">{fmtSol(flow.summary.inSol)} SOL</b> · sent <b className="sell">{fmtSol(flow.summary.outSol)} SOL</b>
              </span>
            )}
          </div>
        )}
        <div className="flow-legend">
          {!stale && legend.map((k) => (
            <span key={k}>
              <i style={{ background: EDGE_STYLE[k].color }} />
              {EDGE_STYLE[k].label}
            </span>
          ))}
          {hasToken && !stale && (
            <span>
              <i className="dash" />
              Token transfer
            </span>
          )}
        </div>
        <button type="button" className="flow-btn" onClick={onToggleFull} title={full ? 'Exit full screen (Esc)' : 'Open full screen'}>
          {full ? '✕ Close' : '⤢ Full screen'}
        </button>
      </div>

      <div ref={canvasRef} className={`flow-canvas ${stale ? 'is-stale' : ''}`} onPointerMove={moveTip}>
        {layout && flow && !stale && (
          <svg
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            width={Math.round(layout.width * scale)}
            height={Math.round(layout.height * scale)}
            className={`flow-svg ${hot ? 'has-hot' : ''}`}
            role="img"
            aria-label="Fund flow diagram"
          >
            <g className="flow-edges">
              {layout.edges.map((le) => {
                const cls = hot ? (hot.edges.has(le.i) ? 'is-hot' : 'is-dim') : '';
                return (
                  <g key={le.i} className={`flow-edge ${cls} ${le.key === 'after_exit' ? 'is-exit' : ''}`} style={{ color: le.color }}>
                    <path d={le.path} className="flow-edge-line" strokeWidth={le.w} strokeDasharray={le.e.kind === 'token' ? '6 4' : undefined} />
                    <path d={le.arrow} className="flow-edge-arrow" />
                    <path
                      d={le.hitPath}
                      className="flow-edge-hit"
                      strokeWidth={Math.max(12, le.w + 8)}
                      onPointerEnter={() => enter({ t: 'edge', i: le.i })}
                      onPointerLeave={leave}
                    />
                  </g>
                );
              })}
            </g>
            <g className="flow-nodes">
              {[...layout.nodes.values()].map((ln) => (
                <FlowNodeBox
                  key={ln.n.address}
                  ln={ln}
                  flow={flow}
                  layout={layout}
                  dims={dims}
                  group={groupOf(ln.n.address)}
                  state={hot ? (hot.nodes.has(ln.n.address) ? 'hot' : 'dim') : undefined}
                  onEnter={enter}
                  onLeave={leave}
                  onOpen={onOpen}
                />
              ))}
            </g>
          </svg>
        )}
        {flow && !flow.edges.length && !stale && <div className="flow-empty">{flow.complete ? 'No SOL or token transfers found for this wallet.' : 'No SOL or token transfers in its latest 200 transactions.'}</div>}
        {(stale || !flow) && (
          // The previous wallet's flow is hidden, not dimmed: a skeleton makes
          // it obvious nothing on screen belongs to the new wallet yet.
          <div className="flow-skel" role="status" aria-live="polite">
            <div className="flow-skel-cols" aria-hidden="true">
              {[3, 1, 3].map((n, c) => (
                <div key={c} className={`flow-skel-col ${c === 1 ? 'mid' : ''}`}>
                  {Array.from({ length: n }, (_, i) => (
                    <i key={i} style={{ animationDelay: `${(c * 3 + i) * 90}ms` }} />
                  ))}
                </div>
              ))}
            </div>
            <div className="flow-skel-label">
              <span className="spinner" /> Tracing money in and out of <b className="mono">{shortAddr(focus, 4, 4)}</b>…
            </div>
          </div>
        )}
        {loading && !stale && flow && (
          <div className="flow-loading">
            <span className="spinner" /> Updating…
          </div>
        )}
        <div ref={tipRef} className={`flow-tip ${hover && flow && !stale ? 'is-on' : ''}`} aria-hidden>
          {hover && flow && layout && <TipContent hover={hover} flow={flow} layout={layout} group={hover.t === 'node' ? groupOf(hover.id) : undefined} />}
        </div>
      </div>

      {flow && !flow.complete && !stale && <div className="flow-note">Busy wallet: only its latest 200 transactions were read.</div>}
    </div>
  );
}

function FlowNodeBox({
  ln,
  flow,
  layout,
  dims,
  group,
  state,
  onEnter,
  onLeave,
  onOpen,
}: {
  ln: LaidNode;
  flow: FundFlow;
  layout: ReturnType<typeof layoutFlow>;
  dims: Dims;
  group?: WalletGroup;
  state?: 'hot' | 'dim';
  onEnter: (h: Hover) => void;
  onLeave: () => void;
  onOpen: (address: string) => void;
}) {
  const { n, x, y, w, h } = ln;
  const isFocus = n.kind === 'focus';
  const isSwap = n.address === SWAP;
  const clickable = !isFocus && !isSwap;
  const accent = isSwap ? KIND.pool.accent : (KIND[n.kind] ?? KIND.unknown).accent;
  const gColor = group ? groupTint(group) : undefined;
  const firstFunder = n.address === flow.summary.firstFunder;

  const title = isSwap ? 'DEX swaps' : (n.label ?? shortAddr(n.address, 5, 5));
  const mono = !isSwap && !n.label;
  const badgeW = group ? 30 : 0;
  const innerW = w - 16 - 10 - (badgeW ? badgeW + 6 : 0);
  const titleTxt = clip(title, Math.floor(innerW / (mono ? dims.mono : dims.sans)));

  const sub = isFocus
    ? n.holderPercent
      ? `holds ${fmtPct(n.holderPercent)} of supply`
      : n.label
        ? shortAddr(n.address)
        : 'selected wallet'
    : isSwap
      ? 'trading of all tokens'
      : [firstFunder ? 'first funder' : '', (KIND[n.kind] ?? KIND.unknown).label, n.holderPercent ? `holds ${fmtPct(n.holderPercent)}` : '']
          .filter(Boolean)
          .join(' · ');
  const subTxt = clip(sub, Math.floor((w - 26) / (dims.sans * 0.9)));

  // amount line: arrows follow the drawn direction of each edge
  const mine = layout.edges.filter((le) => le.e.from === n.address || le.e.to === n.address);
  const amounts: ReactNode[] = [];
  if (isFocus) {
    amounts.push(
      <tspan key="in" fill={EDGE_STYLE.in.color}>
        in {fmtSol(flow.summary.inSol)}
      </tspan>,
      <tspan key="sep" className="flow-sep">
        {'  ·  '}
      </tspan>,
      <tspan key="out" fill={EDGE_STYLE.out.color}>
        out {fmtSol(flow.summary.outSol)} SOL
      </tspan>,
    );
  } else {
    const top = mine
      .slice()
      .sort((x, y2) => y2.e.sol - x.e.sol)
      .slice(0, 2)
      .map((le) => {
        const other = layout.nodes.get(le.e.from === n.address ? le.e.to : le.e.from);
        const rightward = le.e.from === n.address ? (other?.x ?? 0) > x : x > (other?.x ?? 0);
        return { le, arrow: rightward ? '→' : '←' };
      });
    // fit the line: full amounts, then without the unit, then the largest only
    const budget = Math.floor((w - 26) / dims.mono);
    const fmt = (withUnit: boolean) => top.map((t) => `${t.arrow} ${withUnit ? edgeAmount(t.le.e) : edgeAmount(t.le.e).replace(' SOL', '')}`);
    let parts = fmt(true);
    if (parts.join('  ').length > budget) parts = fmt(false);
    if (parts.join('  ').length > budget) parts = parts.slice(0, 1);
    parts.forEach((txt, k) => {
      if (k) amounts.push(<tspan key={`s${k}`} className="flow-sep">{'  '}</tspan>);
      amounts.push(
        <tspan key={top[k]!.le.i} fill={top[k]!.le.color}>
          {clip(txt, budget)}
        </tspan>,
      );
    });
  }

  // centre the 2-3 text lines vertically (nodes grow taller with many edges)
  const lineH = isFocus ? 17 : 15;
  const lines = amounts.length ? 3 : 2;
  const ty = y + h / 2 - (lineH * (lines - 1)) / 2 + 4;

  return (
    <g
      className={`flow-node ${isFocus ? 'is-focus' : ''} ${clickable ? 'is-click' : ''} ${state === 'hot' ? 'is-hot' : state === 'dim' ? 'is-dim' : ''}`}
      onPointerEnter={() => onEnter({ t: 'node', id: n.address })}
      onPointerLeave={onLeave}
      onClick={() => clickable && onOpen(n.address)}
      onKeyDown={(e) => clickable && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen(n.address))}
      tabIndex={clickable ? 0 : undefined}
      role={clickable ? 'button' : undefined}
      aria-label={clickable ? `Follow the money from ${n.label ?? n.address}` : undefined}
    >
      {isFocus && <rect x={x - 4} y={y - 4} width={w + 8} height={h + 8} rx={14} className="flow-focus-halo" />}
      <rect x={x} y={y} width={w} height={h} rx={10} className="flow-box" style={gColor ? { stroke: gColor } : undefined} />
      <rect x={x + 6} y={y + 9} width={3} height={h - 18} rx={1.5} fill={gColor ?? accent} />
      {group && (
        <g>
          <rect x={x + w - badgeW - 8} y={y + 8} width={badgeW} height={16} rx={5} fill={gColor} />
          <text x={x + w - badgeW / 2 - 8} y={y + 19.5} textAnchor="middle" className="flow-gtext">
            G{group.color + 1}
          </text>
        </g>
      )}
      <text x={x + 16} y={ty} className={`flow-ntitle ${mono ? 'mono' : ''}`}>
        {titleTxt}
      </text>
      <text x={x + 16} y={ty + lineH} className="flow-nsub">
        {subTxt}
      </text>
      {amounts.length > 0 && (
        <text x={x + 16} y={ty + lineH * 2} className="flow-namt">
          {amounts}
        </text>
      )}
    </g>
  );
}

function TipContent({ hover, flow, layout, group }: { hover: NonNullable<Hover>; flow: FundFlow; layout: ReturnType<typeof layoutFlow>; group?: WalletGroup }) {
  const line = (le: LaidEdge, perspective?: string) => {
    const e = le.e;
    const from = perspective === e.from ? 'sent' : perspective === e.to ? 'received' : '';
    const peer = perspective === e.from ? e.to : e.from;
    const peerNode = layout.nodes.get(peer)?.n;
    const peerName = peer === SWAP ? 'DEX swaps' : (peerNode?.label ?? shortAddr(peer));
    return (
      <div key={le.i} className="flow-tip-row">
        <i style={{ background: le.color }} />
        <span>
          {perspective ? (
            <>
              {from} <b>{edgeAmount(le.e)}</b> {from === 'sent' ? 'to' : 'from'} {peerName}
            </>
          ) : (
            <>
              <b>{edgeAmount(le.e)}</b>
            </>
          )}
          <span className="faint">
            {' '}
            · {EDGE_STYLE[le.key].label.toLowerCase()}
            {e.firstTs ? ` · ${fmtAge(e.firstTs)} ago` : ''}
            {e.lastTs && e.lastTs !== e.firstTs ? ` → ${fmtAge(e.lastTs)} ago` : ''}
          </span>
        </span>
      </div>
    );
  };

  if (hover.t === 'edge') {
    const le = layout.edges.find((x) => x.i === hover.i);
    if (!le) return null;
    const name = (a: string) => (a === SWAP ? 'DEX swaps' : (layout.nodes.get(a)?.n.label ?? shortAddr(a)));
    return (
      <>
        <div className="flow-tip-h">
          {le.e.kind === 'funding' ? 'First funding' : le.e.kind === 'token' ? 'Token transfer' : 'SOL transfer'}
        </div>
        <div className="flow-tip-sub">
          {name(le.e.from)} → {name(le.e.to)}
        </div>
        {line(le)}
        <div className="faint">{le.e.txCount} transaction{le.e.txCount === 1 ? '' : 's'}</div>
      </>
    );
  }

  const ln = layout.nodes.get(hover.id);
  if (!ln) return null;
  const n = ln.n;
  const isSwap = n.address === SWAP;
  const mine = layout.edges.filter((le) => le.e.from === n.address || le.e.to === n.address).sort((a, b) => b.e.sol - a.e.sol);
  return (
    <>
      <div className="flow-tip-h">
        {isSwap ? 'DEX swaps' : (n.label ?? (n.kind === 'focus' ? 'Selected wallet' : (KIND[n.kind] ?? KIND.unknown).label))}
        {group && (
          <span className="flow-tip-g" style={{ background: groupTint(group) }}>
            G{group.color + 1}
          </span>
        )}
      </div>
      {!isSwap && <div className="flow-tip-addr mono">{n.address}</div>}
      {group && <div className="flow-tip-sub">{group.name}</div>}
      {n.holderPercent ? <div className="flow-tip-sub">holds {fmtPct(n.holderPercent)} of supply</div> : null}
      {n.kind !== 'focus' && mine.slice(0, 4).map((le) => line(le, n.address))}
      {n.kind === 'focus' && (
        <div className="flow-tip-sub">
          received {fmtSol(flow.summary.inSol)} SOL · sent {fmtSol(flow.summary.outSol)} SOL
        </div>
      )}
      {n.kind !== 'focus' && !isSwap && <div className="flow-tip-hint">Click to follow the money from this wallet</div>}
    </>
  );
}
